import { useState, useRef, useCallback, useEffect } from 'react'
import { get } from 'lodash'
import { useAuthentication } from '../../contexts/AuthenticationContext.jsx'
import createShellClient from '../websocket/shell/createShellClient.js'
import { SHELL_CONNECTION_STATE } from '../websocket/constants.js'

// Properly decode base64 to UTF-8 string
const decodeBase64 = (base64Data) => {
  try {
    const byteString = atob(base64Data)
    const bytes = new Uint8Array(byteString.length)
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = byteString.charCodeAt(i)
    }
    const decoder = new TextDecoder('utf-8')
    return decoder.decode(bytes)
  } catch (e) {
    console.error('Failed to decode shell output', e)
    return ''
  }
}

/**
 * Hook for managing multiple shell WebSocket connections.
 * Each session maintains its own connection that stays alive until explicitly closed.
 */
const useShellConnections = () => {
  const { getValidToken } = useAuthentication()

  // Map of sessionId -> { client, terminal, outputBuffer }
  const connectionsRef = useRef(new Map())

  // Connection states per session (triggers re-renders)
  const [connectionStates, setConnectionStates] = useState({})

  // Exit info per session
  const [exitInfos, setExitInfos] = useState({})

  // Errors per session
  const [errors, setErrors] = useState({})

  const updateConnectionState = useCallback((sessionId, state) => {
    setConnectionStates((prev) => ({ ...prev, [sessionId]: state }))
  }, [])

  const updateExitInfo = useCallback((sessionId, exitCode, exitReason) => {
    setExitInfos((prev) => ({ ...prev, [sessionId]: { exitCode, exitReason } }))
  }, [])

  const updateError = useCallback((sessionId, error) => {
    setErrors((prev) => ({ ...prev, [sessionId]: error }))
  }, [])

  const connect = useCallback((sessionId) => {
    if (!sessionId) {
      return Promise.reject(new Error('No session ID'))
    }

    // Already connected or connecting
    const existing = connectionsRef.current.get(sessionId)
    if (existing) {
      const state = connectionStates[sessionId]
      if (state === SHELL_CONNECTION_STATE.CONNECTED || state === SHELL_CONNECTION_STATE.CONNECTING) {
        return Promise.resolve()
      }
    }

    updateConnectionState(sessionId, SHELL_CONNECTION_STATE.CONNECTING)
    setExitInfos((prev) => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
    updateError(sessionId, null)

    // Create handlers for this session
    const handleStarted = () => {
      updateConnectionState(sessionId, SHELL_CONNECTION_STATE.CONNECTED)
      updateError(sessionId, null)
    }

    const handleOutput = (base64Data) => {
      const decoded = decodeBase64(base64Data)
      if (!decoded) return

      const connection = connectionsRef.current.get(sessionId)
      if (connection) {
        if (connection.terminal) {
          connection.terminal.write(decoded)
        } else {
          connection.outputBuffer.push(decoded)
        }
      }
    }

    const handleExit = (code, reason) => {
      updateConnectionState(sessionId, SHELL_CONNECTION_STATE.DISCONNECTED)
      updateExitInfo(sessionId, code, reason)
    }

    const handleError = (errorMsg) => {
      updateConnectionState(sessionId, SHELL_CONNECTION_STATE.ERROR)
      updateError(sessionId, errorMsg)
    }

    const handleConnect = () => {
      updateConnectionState(sessionId, SHELL_CONNECTION_STATE.CONNECTING)
    }

    const handleDisconnect = () => {
      const state = connectionStates[sessionId]
      if (state !== SHELL_CONNECTION_STATE.ERROR) {
        updateConnectionState(sessionId, SHELL_CONNECTION_STATE.DISCONNECTED)
      }
    }

    const client = createShellClient({
      getToken: getValidToken,
      sessionId,
      onStarted: handleStarted,
      onOutput: handleOutput,
      onExit: handleExit,
      onError: handleError,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
    })

    connectionsRef.current.set(sessionId, {
      client,
      terminal: null,
      outputBuffer: [],
    })

    return client.connect()
  }, [getValidToken, connectionStates, updateConnectionState, updateExitInfo, updateError])

  const disconnect = useCallback((sessionId) => {
    const connection = connectionsRef.current.get(sessionId)
    if (connection) {
      connection.client.disconnect()
      connectionsRef.current.delete(sessionId)
    }
    updateConnectionState(sessionId, SHELL_CONNECTION_STATE.DISCONNECTED)

    // Clean up state
    setConnectionStates((prev) => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
    setExitInfos((prev) => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
    setErrors((prev) => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
  }, [updateConnectionState])

  const disconnectAll = useCallback(() => {
    connectionsRef.current.forEach((connection, sessionId) => {
      connection.client.disconnect()
    })
    connectionsRef.current.clear()
    setConnectionStates({})
    setExitInfos({})
    setErrors({})
  }, [])

  const sendInput = useCallback((sessionId, data) => {
    const connection = connectionsRef.current.get(sessionId)
    if (connection) {
      return connection.client.sendInput(data)
    }
    return false
  }, [])

  const sendResize = useCallback((sessionId, cols, rows) => {
    const connection = connectionsRef.current.get(sessionId)
    if (connection) {
      return connection.client.sendResize(cols, rows)
    }
    return false
  }, [])

  const setTerminal = useCallback((sessionId, terminal) => {
    const connection = connectionsRef.current.get(sessionId)
    if (connection) {
      connection.terminal = terminal

      // Flush buffered output if we have a valid terminal
      if (terminal && connection.outputBuffer.length > 0) {
        connection.outputBuffer.forEach((data) => {
          terminal.write(data)
        })
        connection.outputBuffer = []
      }
    }
  }, [])

  const getConnectionState = useCallback((sessionId) => {
    return connectionStates[sessionId] || SHELL_CONNECTION_STATE.DISCONNECTED
  }, [connectionStates])

  const getExitInfo = useCallback((sessionId) => {
    return exitInfos[sessionId] || null
  }, [exitInfos])

  const getError = useCallback((sessionId) => {
    return errors[sessionId] || null
  }, [errors])

  const isConnected = useCallback((sessionId) => {
    return connectionStates[sessionId] === SHELL_CONNECTION_STATE.CONNECTED
  }, [connectionStates])

  const isConnecting = useCallback((sessionId) => {
    return connectionStates[sessionId] === SHELL_CONNECTION_STATE.CONNECTING
  }, [connectionStates])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      connectionsRef.current.forEach((connection) => {
        connection.client.disconnect()
      })
      connectionsRef.current.clear()
    }
  }, [])

  return {
    connect,
    disconnect,
    disconnectAll,
    sendInput,
    sendResize,
    setTerminal,
    getConnectionState,
    getExitInfo,
    getError,
    isConnected,
    isConnecting,
    connectionStates,
  }
}

export default useShellConnections
